import React,{useState,useMemo,useEffect} from 'react';
import s from './Agendamentos.module.css';
import {Btn} from '../UI/Btn';
import {Modal} from '../UI/Modal';
import {Field,Inp,Sel} from '../UI/Inp';
import {Avatar} from '../UI/Avatar';
import {Icons} from '../Sidebar/Icons';
import {useStore} from '../../hooks/useStore';
import {store,today} from '../../utils/store';
import {supabase} from '../../utils/supabase';
import {fmtBRL,fmtDate,fmtMoeda,moedaToNum,openWA,exportCSV} from '../../utils/helpers';

function AgModal({initial,onClose}){
  const {profissionais,servicos,clientes}=useStore();
  const isEdit=!!initial?.id;

  // Cliente selecionado
  const [clienteId,setClienteId]=useState(initial?.clienteId||'');
  const [clienteNome,setClienteNome]=useState(initial?.clienteNome||'');
  const [clienteWa,setClienteWa]=useState(initial?.clienteWa||'');

  // Profissional
  const [profId,setProfId]=useState(initial?.profId||'');

  // Serviços selecionados (múltiplos)
  const [selSvcs,setSelSvcs]=useState(()=>{
    if(!initial?.servico)return[];
    // Tenta recuperar IDs dos serviços pelo nome ao editar
    return[];
  });

  // Data e hora
  const [data,setData]=useState(initial?.data||today());
  const [hora,setHora]=useState(initial?.hora||'');
  const [duracao,setDuracao]=useState(String(initial?.duracao||'30'));

  const [err,setErr]=useState('');
  const [loading,setLoading]=useState(false);
  const [horasOcup,setHorasOcup]=useState([]);
  const [loadingHoras,setLoadingHoras]=useState(false);

  // Busca horários ocupados do profissional na data
  useEffect(()=>{
    if(!data||!profId)return;
    setLoadingHoras(true);
    supabase.from('agendamentos').select('hora')
      .eq('id_admin',store.get().user?.id||store.get().tenant?.id)
      .eq('data',data).eq('prof_id',profId).neq('status','finalizado')
      .then(({data:rows})=>{
        setHorasOcup((rows||[]).map(r=>r.hora));
        setLoadingHoras(false);
      });
  },[data,profId]);

  // Serviços do profissional selecionado
  const prof=profissionais.find(p=>p.id===profId);
  const svcsProf=servicos.filter(sv=>(prof?.sids||[]).includes(sv.id));

  // Quando muda profissional, limpa serviços
  const handleProfId=v=>{setProfId(v);setSelSvcs([]);};

  // Seleciona/deseleciona serviço
  const togSvc=id=>setSelSvcs(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);

  // Calcula total e duração dos serviços selecionados
  const totalValor=selSvcs.reduce((s,id)=>{const v=servicos.find(x=>x.id===id);return s+(v?Number(v.preco):0);},0);
  const totalDur=selSvcs.reduce((s,id)=>{const v=servicos.find(x=>x.id===id);return s+(v?Number(v.dur):0);},0);

  // Quando seleciona cliente do select
  const handleCliente=id=>{
    setClienteId(id);
    if(!id){setClienteNome('');setClienteWa('');return;}
    const c=clientes.find(x=>x.id===id);
    if(c){setClienteNome(c.nome);setClienteWa(c.wa||'');}
  };

  const handleSave=async()=>{
    setErr('');
    if(!clienteNome.trim()){setErr('Selecione ou informe o cliente.');return;}
    if(!data||!hora){setErr('Informe data e hora.');return;}
    if(selSvcs.length===0&&!isEdit){setErr('Selecione ao menos um serviço.');return;}

    const nomesSvcs=selSvcs.map(id=>servicos.find(x=>x.id===id)?.nome||'').filter(Boolean).join(', ');
    const payload={
      clienteNome:clienteNome.trim(),
      clienteWa:clienteWa||'',
      profId:profId||null,
      servico:nomesSvcs||initial?.servico||'',
      valor:totalValor||0,
      duracao:totalDur||Number(duracao)||30,
      data,hora,
      followUp:null,
    };
    setLoading(true);
    try{
      if(isEdit) await store.updateAgendamento(initial.id,payload);
      else await store.addAgendamento(payload);
      onClose();
    }catch(e){setErr(e.message);}
    finally{setLoading(false);}
  };

  return(
    <Modal title={isEdit?'Editar Agendamento':'Novo Agendamento'} onClose={onClose}
      footer={<><Btn variant="secondary" onClick={onClose}>Cancelar</Btn><Btn onClick={handleSave} disabled={loading}>{loading?'Salvando...':'Salvar'}</Btn></>}
      maxWidth={520}>
      {err&&<div style={{background:'var(--red-l)',color:'var(--red)',border:'0.5px solid #fca5a5',borderRadius:8,padding:'10px 14px',fontSize:13,marginBottom:14}}>⚠️ {err}</div>}

      {/* Cliente — select dos cadastrados */}
      <Field label="Cliente" required>
        <Sel value={clienteId} onChange={e=>handleCliente(e.target.value)}>
          <option value="">— Selecionar cliente —</option>
          {clientes.map(c=><option key={c.id} value={c.id}>{c.nome}{c.wa?` • ${c.wa}`:''}</option>)}
        </Sel>
        {clienteId&&clienteWa&&(
          <div style={{fontSize:11,color:'var(--g400)',marginTop:4}}>WhatsApp: {clienteWa}</div>
        )}
        {clientes.length===0&&(
          <div style={{fontSize:11,color:'var(--amber,#d97706)',marginTop:4}}>Nenhum cliente cadastrado. Cadastre primeiro na aba Clientes.</div>
        )}
      </Field>

      {/* Profissional */}
      <Field label="Profissional">
        <Sel value={profId} onChange={e=>handleProfId(e.target.value)}>
          <option value="">— Selecionar profissional —</option>
          {profissionais.map(p=><option key={p.id} value={p.id}>{p.nome}</option>)}
        </Sel>
      </Field>

      {/* Serviços do profissional — múltipla seleção */}
      <Field label="Serviços" required>
        {!profId?(
          <div style={{fontSize:12,color:'var(--g400)',padding:'10px 0'}}>Selecione um profissional primeiro.</div>
        ):svcsProf.length===0?(
          <div style={{fontSize:12,color:'var(--g400)',padding:'10px 0'}}>Nenhum serviço vinculado a este profissional.</div>
        ):(
          <div style={{display:'grid',gap:6}}>
            {svcsProf.map(sv=>{
              const sel=selSvcs.includes(sv.id);
              return(
                <div key={sv.id} onClick={()=>togSvc(sv.id)} style={{
                  display:'flex',justifyContent:'space-between',alignItems:'center',
                  padding:'10px 12px',borderRadius:'var(--r-md)',cursor:'pointer',
                  border:`0.5px solid ${sel?'var(--blue)':'var(--g200)'}`,
                  background:sel?'var(--blue-l)':'#fff',transition:'all .12s',
                }}>
                  <div>
                    <div style={{fontSize:13,fontWeight:500,color:'var(--g800)'}}>{sv.nome}</div>
                    <div style={{fontSize:11,color:'var(--g400)',marginTop:2}}>{sv.dur}min</div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <span style={{fontSize:13,fontWeight:700,color:sel?'var(--blue)':'var(--green)'}}>{fmtBRL(sv.preco)}</span>
                    <div style={{width:18,height:18,borderRadius:'50%',border:`1.5px solid ${sel?'var(--blue)':'var(--g300)'}`,background:sel?'var(--blue)':'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      {sel&&<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {selSvcs.length>0&&(
          <div style={{background:'var(--green-l)',border:'0.5px solid #86efac',borderRadius:8,padding:'8px 12px',marginTop:8,fontSize:12.5,color:'var(--green)',fontWeight:500}}>
            {selSvcs.length} serviço{selSvcs.length>1?'s':''} • Total: {fmtBRL(totalValor)} • {totalDur}min
          </div>
        )}
      </Field>

      {/* Data */}
      <Field label="Data" required>
        <Inp type="date" value={data} onChange={e=>{setData(e.target.value);setHora('');}}/>
      </Field>

      {/* Horários disponíveis */}
      <Field label="Horário" required>
        {(!profId||!data)?(
          <div style={{fontSize:12,color:'var(--g400)',padding:'8px 0'}}>
            Selecione o profissional e a data para ver os horários disponíveis.
          </div>
        ):loadingHoras?(
          <div style={{fontSize:12,color:'var(--g400)',padding:'8px 0'}}>Carregando horários...</div>
        ):(()=>{
          // Pega horários configurados pelo admin
          const {horarios}=store.get();
          const MDIA=['dom','seg','ter','qua','qui','sex','sab'];
          const parts=data.split('-').map(Number);
          const dow=new Date(parts[0],parts[1]-1,parts[2]).getDay();
          const diaNome=MDIA[dow];
          const diaVal=horarios?.[diaNome];
          let listaHoras=[];
          if(diaVal&&typeof diaVal==='object'&&!Array.isArray(diaVal)&&diaVal.ativo){
            listaHoras=[...(diaVal.horarios||[])].sort();
          } else if(diaVal===true){
            const [hI,mI]=(horarios.ini||'09:00').split(':').map(Number);
            const [hF,mF]=(horarios.fim||'18:00').split(':').map(Number);
            let c=hI*60+mI;
            while(c<=hF*60+mF){listaHoras.push(`${String(Math.floor(c/60)).padStart(2,'0')}:${String(c%60).padStart(2,'0')}`);c+=30;}
          }
          // Filtra horários passados se for hoje
          const tstr=today();
          if(data===tstr){
            const agora=new Date();
            const agoraMin=agora.getHours()*60+agora.getMinutes();
            listaHoras=listaHoras.filter(h=>{const [hh,mm]=h.split(':').map(Number);return hh*60+mm>agoraMin;});
          }
          if(listaHoras.length===0) return(
            <div style={{fontSize:12,color:'var(--g400)',padding:'8px 0'}}>
              Nenhum horário cadastrado para este dia. Configure em <strong>Horários</strong>.
            </div>
          );
          return(
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(72px,1fr))',gap:7}}>
              {listaHoras.map(h=>{
                const ocup=horasOcup.includes(h);
                const sel=hora===h;
                return(
                  <div key={h} onClick={()=>!ocup&&setHora(h)} style={{
                    padding:'9px 6px',textAlign:'center',borderRadius:'var(--r-md)',
                    fontSize:13,fontWeight:sel?700:400,cursor:ocup?'not-allowed':'pointer',
                    border:`0.5px solid ${sel?'var(--blue)':ocup?'var(--g200)':'var(--g200)'}`,
                    background:sel?'var(--blue)':ocup?'var(--g50)':'#fff',
                    color:sel?'#fff':ocup?'var(--g300)':'var(--g700)',
                    transition:'all .12s',position:'relative',
                  }}>
                    {h}
                    {ocup&&<div style={{fontSize:9,color:'var(--g300)',marginTop:2}}>ocupado</div>}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </Field>
    </Modal>
  );
}

// Calcula o status temporal do agendamento em relação à hora atual
function getStatusHora(data, hora, tstr) {
  if (data !== tstr) return 'normal'; // não é hoje
  const agora = new Date();
  const [h, m] = hora.split(':').map(Number);
  const agMin = h * 60 + m;
  const agoraMin = agora.getHours() * 60 + agora.getMinutes();
  const diff = agMin - agoraMin;
  if (diff < -30)  return 'passou';      // passou há mais de 30min
  if (diff < 0)    return 'acontecendo'; // está acontecendo agora
  if (diff <= 30)  return 'em_breve';    // em até 30min
  if (diff <= 60)  return 'proximo';     // em até 1h
  return 'normal';
}

const STATUS_ESTILO = {
  passou:      { bg: '#f8fafc', border: '#e2e8f0', opacity: 0.7 },
  acontecendo: { bg: '#eff6ff', border: '#3b82f6' },
  em_breve:    { bg: '#fff7ed', border: '#f97316' },
  proximo:     { bg: '#fefce8', border: '#eab308' },
  normal:      { bg: '#fff',    border: '#e5e7eb' },
};

const STATUS_BADGE = {
  passou:      null,
  acontecendo: { txt: '🟢 Agora',    cor: '#1d4ed8', bg: '#dbeafe' },
  em_breve:    { txt: '🔥 Em breve', cor: '#c2410c', bg: '#ffedd5' },
  proximo:     { txt: '⏰ Em 1h',    cor: '#854d0e', bg: '#fef9c3' },
  normal:      null,
};

const TABS=['hoje','todos','finalizados'];
const TLBL={hoje:'Hoje',todos:'Todos',finalizados:'Finalizados'};
const PER=10;

export function Agendamentos(){
  const {agendamentos,profissionais}=useStore();
  const [tab,setTab]=useState('hoje');
  const [q,setQ]=useState('');
  const [fp,setFp]=useState('');
  const [page,setPage]=useState(1);
  const [modal,setModal]=useState(null);
  const [finId,setFinId]=useState(null);
  const tstr=today();

  const filtered=useMemo(()=>agendamentos.filter(a=>{
    const mt=tab==='hoje'?a.data===tstr&&a.status!=='finalizado':tab==='finalizados'?a.status==='finalizado':a.status!=='finalizado';
    const mq=!q||a.clienteNome?.toLowerCase().includes(q.toLowerCase())||a.servico?.toLowerCase().includes(q.toLowerCase());
    const mp=!fp||a.profId===fp;
    return mt&&mq&&mp;
  }).sort((a,b)=>a.data+a.hora>b.data+b.hora?1:-1),[agendamentos,tab,q,fp,tstr]);

  const pages=Math.max(1,Math.ceil(filtered.length/PER));
  const paged=filtered.slice((page-1)*PER,page*PER);
  const counts={
    hoje:agendamentos.filter(a=>a.data===tstr&&a.status!=='finalizado').length,
    todos:agendamentos.filter(a=>a.status!=='finalizado').length,
    finalizados:agendamentos.filter(a=>a.status==='finalizado').length,
  };

  const del=async id=>{if(window.confirm('Excluir?'))await store.deleteAgendamento(id);};
  const fin=async id=>{await store.finalizarAgendamento(id);setFinId(null);};
  const exp=()=>{
    const h=['"Cliente"','"Profissional"','"Serviço"','"Data"','"Hora"','"Valor"'];
    const r=filtered.map(a=>{const p=profissionais.find(x=>x.id===a.profId);return[`"${a.clienteNome}"`,`"${p?.nome||''}"`,`"${a.servico}"`,`"${fmtDate(a.data)}"`,`"${a.hora}"`,`"R$ ${Number(a.valor).toFixed(2).replace('.',',')}"`].join(';');});
    exportCSV('agendamentos.csv',h,r);
  };

  return(
    <div>

      {/* Cabeçalho: tabs + botão novo */}
      <div className={s.header}>
        <div className={s.tabs}>
          {TABS.map(t=>(
            <button key={t} className={`${s.tab} ${tab===t?s.on:''}`} onClick={()=>{setTab(t);setPage(1);}}>
              {TLBL[t]}<span className={s.cnt}>{counts[t]}</span>
            </button>
          ))}
        </div>
        <Btn icon={<Icons.Plus/>} onClick={()=>setModal({})}>Novo agendamento</Btn>
      </div>

      {/* Barra de filtros */}
      <div className={s.bar}>
        <div className={s.srch}>
          <Icons.Search/>
          <input placeholder="Buscar cliente ou serviço..." value={q} onChange={e=>{setQ(e.target.value);setPage(1);}}/>
        </div>
        <div className={s.selWrap}>
          <select value={fp} onChange={e=>setFp(e.target.value)}>
            <option value="">Todos profissionais</option>
            {profissionais.map(p=><option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
        </div>
        <Btn variant="secondary" size="sm" icon={<Icons.Download/>} onClick={exp}>CSV</Btn>
      </div>

      {/* Tabela */}
      <div className={s.card}><div className={s.tw}>
        <table className={s.tb}>
          <thead><tr><th>Cliente</th><th>Profissional</th><th>Serviço</th><th>Data/Hora</th><th>Valor</th><th>Ações</th></tr></thead>
          <tbody>
            {paged.length===0&&(
              <tr><td colSpan={6}>
                <div className={s.emp}>
                  <Icons.Calendar/>
                  <p>Nenhum agendamento.</p>
                  {tab!=='finalizados'&&<Btn size="sm" onClick={()=>setModal({})}>Criar agendamento</Btn>}
                </div>
              </td></tr>
            )}
            {paged.map(a=>{
              const pi=profissionais.findIndex(x=>x.id===a.profId);
              const p=profissionais.find(x=>x.id===a.profId);
              const stHora=tab==='hoje'?getStatusHora(a.data,a.hora,tstr):'normal';
              const estilo=STATUS_ESTILO[stHora];
              const badge=STATUS_BADGE[stHora];
              const destaque=stHora==='em_breve'||stHora==='acontecendo';
              return(
                <tr key={a.id} style={{
                  background:estilo.bg,
                  outline:destaque?`1.5px solid ${estilo.border}`:'none',
                  outlineOffset:-1,
                  opacity:estilo.opacity||1,
                  transition:'background .3s',
                }}>
                  <td>
                    <div className={s.cf}>
                      <Avatar name={a.clienteNome} idx={0} size={32}/>
                      <div>
                        <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                          <div className={s.cn} style={{opacity:stHora==='passou'?.6:1}}>{a.clienteNome}</div>
                          {badge&&<span style={{background:badge.bg,color:badge.cor,borderRadius:20,padding:'2px 8px',fontSize:11,fontWeight:700,whiteSpace:'nowrap'}}>{badge.txt}</span>}
                        </div>
                        {a.clienteWa&&(
                          <button
                            className={s.wa}
                            onClick={()=>openWA(a.clienteWa)}
                            style={destaque?{
                              background:'#16a34a',color:'#fff',
                              border:'none',padding:'4px 12px',
                              fontSize:12.5,fontWeight:600,
                            }:{}}
                          >
                            <Icons.WA/>{a.clienteWa}
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    {p
                      ?<div className={s.cf}><Avatar name={p.nome} idx={pi} size={28}/><span>{p.nome}</span></div>
                      :<span className={s.dash}>—</span>
                    }
                  </td>
                  <td><div className={s.sv}>{a.servico}</div></td>
                  <td>
                    <div className={s.cn} style={{opacity:stHora==='passou'?.6:1}}>{fmtDate(a.data)}</div>
                    <div className={s.cs} style={{fontWeight:destaque?700:'normal',color:destaque?estilo.border:'var(--g400)'}}>{a.hora}{a.duracao?` • ${a.duracao}min`:''}</div>
                  </td>
                  <td><strong style={{color:'var(--green)'}}>{fmtBRL(a.valor)}</strong></td>
                  <td>
                    <div className={s.acts}>
                      {a.status!=='finalizado'&&(
                        <Btn variant="success" size="sm" onClick={()=>setFinId(a.id)}>✓</Btn>
                      )}
                      <button className={s.ab} onClick={()=>setModal(a)}><Icons.Edit/></button>
                      <button className={`${s.ab} ${s.abd}`} onClick={()=>del(a.id)}><Icons.Trash/></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div></div>

      {/* Paginação */}
      {pages>1&&(
        <div className={s.pag}>
          {Array.from({length:pages},(_,i)=>(
            <button key={i} className={`${s.pg} ${page===i+1?s.pgOn:''}`} onClick={()=>setPage(i+1)}>{i+1}</button>
          ))}
        </div>
      )}

      {modal!==null&&<AgModal initial={modal?.id?modal:null} onClose={()=>setModal(null)}/>}
      {finId&&(
        <Modal title="Finalizar atendimento?" onClose={()=>setFinId(null)}
          footer={<><Btn variant="secondary" onClick={()=>setFinId(null)}>Cancelar</Btn><Btn variant="success" onClick={()=>fin(finId)}>Finalizar</Btn></>}
          maxWidth={380}>
          <p style={{fontSize:14,color:'var(--g600)'}}>O agendamento será marcado como finalizado e movido para o histórico.</p>
        </Modal>
      )}
    </div>
  );
}
